// /src/hooks/useImageUploader.js
import { useState } from 'react';
import { ref, uploadBytes } from 'firebase/storage';
import { storage } from './firebase';
import heic2any from 'heic2any';

export default function useImageUploader(userId) {
  const [uploading, setUploading] = useState(false);
  const [imagePath, setImagePath] = useState(null);
  const [thumbnailPath, setThumbnailPath] = useState(null);
  const [imageSize, setImageSize] = useState(null);
  const [thumbnailSize, setThumbnailSize] = useState(null);
  const [originalFilePath, setOriginalFilePath] = useState(null);
  const [originalFileSize, setOriginalFileSize] = useState(null);
  const [originalMimeType, setOriginalMimeType] = useState(null);

  const convertIfHeic = async (file) => {
    if (file.type === 'image/heic' || file.name?.toLowerCase().endsWith('.heic')) {
      try {
        const convertedBlob = await heic2any({
          blob: file,
          toType: 'image/jpeg',
          quality: 0.9,
        });
        return new File([convertedBlob], file.name.replace(/\.heic$/, '.jpg'), { type: 'image/jpeg' });
      } catch (err) {
        console.error('HEIC conversion failed', err);
        throw new Error('Failed to convert HEIC image.');
      }
    }
    return file;
  };

  const uploadImage = async (file) => {
    if (!file || !userId) return;
    setUploading(true);

    const timestamp = Date.now();
    const basePath = `thoughts/${userId}/${timestamp}`;

    try {
      const originalFile = await convertIfHeic(file);
      const originalRef = ref(storage, `${basePath}-original.${originalFile.name.split('.').pop()}`);
      await uploadBytes(originalRef, originalFile);
      setOriginalFilePath(originalRef.fullPath);
      setOriginalFileSize(originalFile.size);
      setOriginalMimeType(originalFile.type);

      const fullBlob = await createResizedBlob(originalFile, 1600);
      const fullRef = ref(storage, `${basePath}-full.webp`);
      await uploadBytes(fullRef, fullBlob);
      setImagePath(fullRef.fullPath);
      setImageSize(fullBlob.size);

      const thumbBlob = await createThumbnailBlob(originalFile);
      const thumbRef = ref(storage, `${basePath}-thumb.webp`);
      await uploadBytes(thumbRef, thumbBlob);
      setThumbnailPath(thumbRef.fullPath);
      setThumbnailSize(thumbBlob.size);
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setUploading(false);
    }
  };

  const createResizedBlob = (file, maxWidth) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const scale = Math.min(1, maxWidth / img.width);
          const canvas = document.createElement('canvas');
          canvas.width = img.width * scale;
          canvas.height = img.height * scale;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          canvas.toBlob((blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Canvas blob generation failed'));
          }, 'image/webp', 0.85);
        };
        img.onerror = reject;
        img.src = event.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const createThumbnailBlob = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          if (img.width <= 500) {
            // Use the original file if it's already small
            resolve(file);
            return;
          }

          const maxSize = 600;
          const scale = Math.min(maxSize / img.width, maxSize / img.height);
          const dpr = window.devicePixelRatio || 1;

          const canvas = document.createElement('canvas');
          canvas.width = img.width * scale * dpr;
          canvas.height = img.height * scale * dpr;

          const ctx = canvas.getContext('2d');
          ctx.scale(dpr, dpr);
          ctx.drawImage(img, 0, 0, img.width * scale, img.height * scale);

          canvas.toBlob((blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Canvas blob generation failed'));
          }, 'image/webp', 0.85);
        };
        img.onerror = reject;
        img.src = event.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const resetUploads = () => {
    setImagePath(null);
    setThumbnailPath(null);
    setOriginalFilePath(null);
    setOriginalFileSize(null);
    setOriginalMimeType(null);
  };

  return {
    uploadImage,
    uploading,
    imagePath,
    thumbnailPath,
    imageSize,
    thumbnailSize,
    originalFilePath,
    originalFileSize,
    originalMimeType,
    resetUploads,
  };
}